import { ComponentProps, ComponentType, FC, useEffect, useState } from 'react'
import { BrowserRouter as Router, Switch, Redirect, matchPath, useLocation } from 'react-router-dom'
import { routes } from 'routes'
import { useTrybeApiSetup } from 'utilities/TrybeApiClient'
import 'utilities/fontAwesomeLibrary'
import { useUser } from 'hooks/UseUser'
import { OrganisationConfigProvider } from 'contexts/OrganisationConfig'
import { SpinnerOverlay } from 'components/SpinnerOverlay'
import { withSiteConfig } from 'contexts/SiteConfig'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { createBrowserHistory } from 'history'
import { ErrorFallback } from 'components/ErrorFallback'
import { ReactQueryDevtools } from 'react-query/devtools'
import { useCurrentUserSitePermissions } from 'api/UserSiteRoles'
import { HTML5Backend as Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import { withNotifications } from 'ui/components/Notification'
import SidebarLayout from 'layouts/SidebarLayout/SidebarLayout'
import { GlobalBarcodeListenerProvider } from 'components/BarcodeListener/GlobalContext'
import 'react-phone-number-input/style.css'
import { oidcConfiguration } from 'constants/oidcConfiguration'
import { Authenticating, NotAuthorized } from 'components/Authentication'
import { AuthProvider, hasAuthParams, useAuth } from 'react-oidc-context'
import { PromptForUpdate } from 'components/PromptForUpdate'
import { GantnerUsageNotification } from 'components/GantnerUsageNotification'
import { NotFound404 } from 'screens/NotFound404'
import { Log } from 'oidc-client-ts'
import { Intercom } from 'components/Intercom'
import { ExpiredRedirect } from 'components/ExpiredRedirect'
import { Analytics } from 'utilities/analytics'
import { Vitally } from 'components/Vitally'
import { noop } from 'lodash'
import { withQueryClient } from 'contexts/QueryClientProvider'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import { Settings as DateTimeSettings } from 'luxon'
import type { Locale } from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { enGB } from 'date-fns/locale/en-GB'

const userLocale = Intl.DateTimeFormat().resolvedOptions().locale

// Set Luxon's default locale to the user's system locale
DateTimeSettings.defaultLocale = userLocale

// Map of supported locales for react-datepicker
const locales: Record<string, Locale> = {
  'en-US': enUS,
  'en-GB': enGB,
}

Object.entries(locales).forEach(([key, locale]) => {
  registerLocale(key, locale)
})

const supportedLocale = locales[userLocale] ? userLocale : 'en-GB'

// Set react-datepicker's default locale to the user's system locale if supported, otherwise default to 'en-GB'
setDefaultLocale(supportedLocale)

const App = () => {
  const { default_organisation_id: organisationId } = useUser()

  return (
    <OrganisationConfigProvider organisationId={organisationId}>
      {/* @ts-expect-error Does not support react@18 types where `children` prop type was removed. */}
      <DndProvider backend={Backend}>
        <IfLoadedPermissions>
          <GantnerUsageNotification />
          <PromptForUpdate />
          <GlobalBarcodeListenerProvider>
            <SidebarLayout>
              <Switch>
                {routes
                  .filter((route) => 'component' in route)
                  .map((route, index) => (
                    <route.route
                      key={index}
                      path={route.path}
                      exact={route.exact}
                      permissions={route.permissions}
                      title={route.name}
                      component={(props) => <route.component {...props} />}
                    />
                  ))}
                <DefaultRedirect />
              </Switch>
            </SidebarLayout>
            <Intercom />
            <Vitally />
            <Analytics />
          </GlobalBarcodeListenerProvider>

          {import.meta.env.VITE_APP_ENABLE_REACT_QUERY_DEV_TOOLS && (
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          )}
        </IfLoadedPermissions>
      </DndProvider>
    </OrganisationConfigProvider>
  )
}

const IfLoadedPermissions = ({ children }) => {
  const { isSuccess, isError, error } = useCurrentUserSitePermissions()

  if (isError && error?.response?.status === 403) return <NotAuthorized />

  return !isSuccess ? <SpinnerOverlay /> : children
}

const DefaultRedirect = () => {
  const { pathname } = useLocation()

  // If this is a /beta route, let's try redirecting to the non-beta version.
  if (pathname.startsWith('/beta')) {
    return <Redirect to={pathname.replace('/beta', '')} />
  }

  return <NotFound404 />
}

const history = createBrowserHistory()

if (import.meta.env.VITE_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_PROJECT_ENVIRONMENT,
    release: import.meta.env.VITE_APP_VERSION,
    // @ts-expect-error
    integrations: [
      import.meta.env.VITE_APP_PROJECT_ENVIRONMENT === 'staging'
        ? Sentry.replayIntegration()
        : null,
      new Integrations.BrowserTracing({
        routingInstrumentation() {
          return Sentry.reactRouterV5Instrumentation(history, routes as any, matchPath)
        },
      }),
    ].filter(Boolean),
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1,
    tracesSampleRate: 0.01,
    ignoreErrors: [
      /^Loading (CSS )?chunk \d* failed\./,
      /^Failed to fetch dynamically imported module/,
      /^Illegal invocation$/,
      /ResizeObserver loop completed with undelivered notifications/,
      /ResizeObserver loop limit exceeded$/,
      /Failed to update a ServiceWorker for scope/,
      /\.try\.be\/service-worker\.js load failed/,
      /Load failed/,
      /InvalidStateError: Failed to update a ServiceWorker for scope/,
      /Importing a module script failed./,
      /Unexpected end of JSON input/,
      /Cannot update a null\/nonexistent service worker registration/,
    ],
  })
}

function withAuthentication<C extends ComponentType<any>>(Component: C): FC<ComponentProps<C>> {
  return (props) => {
    return (
      <AuthProvider
        {...oidcConfiguration}
        onSigninCallback={(user: any) => {
          window.history.replaceState({}, document.title, user.state || window.location.pathname)
        }}
      >
        <Component {...props} />
      </AuthProvider>
    )
  }
}

const useUserManagerEvents = () => {
  const auth = useAuth()

  Log.setLevel(Log.INFO)
  Log.setLogger(console)

  useEffect(() => {
    if (import.meta.env.NODE_ENV !== 'production') auth.events.addUserLoaded(noop)
  }, [auth.events])

  useEffect(() => {
    if (import.meta.env.NODE_ENV !== 'production') auth.events.addUserUnloaded(noop)
  }, [auth.events])

  useEffect(() => {
    if (import.meta.env.NODE_ENV !== 'production') auth.events.addUserSignedIn(noop)
  }, [auth.events])

  useEffect(() => {
    if (import.meta.env.NODE_ENV !== 'production') auth.events.addUserSignedOut(noop)
  }, [auth.events])

  useEffect(() => {
    if (import.meta.env.NODE_ENV !== 'production') auth.events.addUserSessionChanged(noop)
  }, [auth.events])
}

function withAuthorisation<C extends ComponentType<any>>(Component: C): FC<ComponentProps<C>> {
  return (props) => {
    const auth = useAuth()
    const [hasTriedSignin, setHasTriedSignin] = useState(false)
    const [hasAddedEvent, setHasAddedEvent] = useState(false)

    // automatically sign-in
    useEffect(() => {
      if (
        !hasAuthParams() &&
        !auth.isAuthenticated &&
        !auth.activeNavigator &&
        !auth.isLoading &&
        !hasTriedSignin
      ) {
        auth.signinRedirect({ state: window.location.href })
        setHasTriedSignin(true)
      }
    }, [auth, hasTriedSignin])

    // https://github.com/authts/react-oidc-context/issues/390#issue-1249428231
    useEffect(() => {
      if (!hasAddedEvent) {
        auth.events.addAccessTokenExpiring(() => {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Starting silent renew',
          })

          auth.startSilentRenew()
        })

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Added accessTokenExpiring event callback',
        })

        setHasAddedEvent(true)

        auth.events.addSilentRenewError((error) => {
          Sentry.captureException(error, { extra: { category: 'auth', event: 'silentRenewError' } })

          if (auth.user?.expired) {
            auth.removeUser()
            auth.signoutRedirect()
          }
        })
      }
    }, [auth, hasAddedEvent])

    useEffect(() => {
      if (!auth.isAuthenticated || !auth.user?.profile.sub) return

      Sentry.setTag('trybe_user_id', auth.user.profile.sub)
    }, [auth.isAuthenticated, auth.user?.profile.sub])

    useUserManagerEvents()

    switch (auth.activeNavigator) {
      case 'signinSilent':
        return <div>Signing you in...</div>
      case 'signoutRedirect':
        return <div></div>
    }

    if (auth.isLoading) {
      return <Authenticating />
    }

    if (auth.error) {
      if (auth.user?.expired) {
        return <ExpiredRedirect />
      }

      return <NotAuthorized />
    }

    if (!auth.isAuthenticated) {
      return null
    }

    return <Component {...props} />
  }
}

function withSdkConfiguration<C extends ComponentType<any>>(Component: C): FC<ComponentProps<C>> {
  return (props) => {
    useTrybeApiSetup()

    return <Component {...props} />
  }
}

function withRouter<C extends ComponentType<any>>(Component: C): FC<ComponentProps<C>> {
  return (props) => {
    return (
      <Router>
        <Component {...props} />
      </Router>
    )
  }
}

export default Sentry.withProfiler(
  withAuthentication(
    withAuthorisation(
      withSdkConfiguration(
        withQueryClient(
          withRouter(
            withSiteConfig(
              withNotifications(
                Sentry.withErrorBoundary(App, { fallback: (props) => <ErrorFallback {...props} /> })
              )
            )
          )
        )
      )
    )
  )
)
